import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RevealOnScroll } from "./Reveal";
import { MENU } from "@/constants/testIds";
import { MENU_CATEGORIES, AGGIUNTE, SITE } from "@/data/siteData";
import { Pizza, Circle } from "lucide-react";

const MenuSection = () => {
    const [format, setFormat] = useState("taglio"); // 'taglio' | 'rotonda'
    const [active, setActive] = useState(MENU_CATEGORIES[0].slug);
    const activeCat = MENU_CATEGORIES.find((c) => c.slug === active);
    const isTaglio = format === "taglio";
    const isSfizio = active === "sfizioserie";

    return (
        <section
            id="menu"
            data-testid={MENU.section}
            className="relative bg-[#0a0a0a] text-[#f4f4ee] py-20 md:py-28"
        >
            <div className="px-5 md:px-10 lg:px-14 max-w-[1600px] mx-auto">
                <div className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-white/25 pb-8">
                    <div>
                        <span className="text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-[#7BB53E]">
                            · Il Menu
                        </span>
                        <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] mt-3">
                            Scegli il tuo
                            <br />
                            <span className="italic font-serif-editorial text-[#FFD600] normal-case">
                                percorso.
                            </span>
                        </h2>
                    </div>
                    <p className="max-w-md text-base md:text-lg text-white/70">
                        Tutti i gusti si possono ordinare in due formati: al{" "}
                        <span className="text-[#FFD600] font-semibold">taglio</span> a
                        prezzo fisso, oppure{" "}
                        <span className="text-[#FFD600] font-semibold">rotonda</span> con
                        prezzo variabile per gusto.
                    </p>
                </div>

                {/* Format toggle — TAGLIO vs ROTONDA */}
                <RevealOnScroll>
                    <div
                        role="tablist"
                        aria-label="Formato pizza"
                        className="relative inline-flex items-stretch p-1.5 rounded-full bg-white/8 border-2 border-white/30 mb-8 md:mb-10"
                    >
                        <button
                            role="tab"
                            aria-selected={isTaglio}
                            data-testid="menu-format-taglio"
                            onClick={() => setFormat("taglio")}
                            className={`relative z-10 inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2.5 md:py-3 rounded-full transition-colors duration-300 ${
                                isTaglio ? "text-[#0a0a0a]" : "text-white/70 hover:text-white"
                            }`}
                        >
                            {isTaglio && (
                                <motion.span
                                    layoutId="format-pill"
                                    className="absolute inset-0 rounded-full bg-[#7BB53E]"
                                    transition={{ type: "spring", stiffness: 500, damping: 40 }}
                                />
                            )}
                            <Pizza className="relative z-10 w-4 h-4" strokeWidth={2.5} />
                            <span className="relative z-10 font-display uppercase text-sm md:text-base tracking-wider">
                                Al Taglio
                            </span>
                            <span
                                className={`relative z-10 hidden sm:inline-block text-xs font-bold px-2 py-0.5 rounded-full ${
                                    isTaglio
                                        ? "bg-[#0a0a0a] text-[#FFD600]"
                                        : "bg-white/10 text-white/70"
                                }`}
                            >
                                € {SITE.tagliePrice} · tutti i gusti
                            </span>
                        </button>
                        <button
                            role="tab"
                            aria-selected={!isTaglio}
                            data-testid="menu-format-rotonda"
                            onClick={() => setFormat("rotonda")}
                            className={`relative z-10 inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2.5 md:py-3 rounded-full transition-colors duration-300 ${
                                !isTaglio ? "text-white" : "text-white/70 hover:text-white"
                            }`}
                        >
                            {!isTaglio && (
                                <motion.span
                                    layoutId="format-pill"
                                    className="absolute inset-0 rounded-full bg-[#E63946]"
                                    transition={{ type: "spring", stiffness: 500, damping: 40 }}
                                />
                            )}
                            <Circle className="relative z-10 w-4 h-4" strokeWidth={2.5} />
                            <span className="relative z-10 font-display uppercase text-sm md:text-base tracking-wider">
                                Rotonda
                            </span>
                        </button>
                    </div>
                </RevealOnScroll>

                {/* Category tabs */}
                <div className="flex flex-wrap gap-3 md:gap-4 mb-10 md:mb-12">
                    {MENU_CATEGORIES.map((cat) => {
                        const isActive = cat.slug === active;
                        return (
                            <button
                                key={cat.slug}
                                data-testid={MENU.tab(cat.slug)}
                                onClick={() => setActive(cat.slug)}
                                className={`px-5 md:px-6 py-2.5 md:py-3 rounded-full border-2 font-bold uppercase tracking-widest text-xs md:text-sm transition-colors duration-300 ${
                                    isActive
                                        ? "bg-[#FFD600] text-[#0a0a0a] border-[#FFD600]"
                                        : "bg-transparent text-white border-white/50 hover:border-white"
                                }`}
                            >
                                {cat.name}
                            </button>
                        );
                    })}
                </div>

                {/* Items */}
                <AnimatePresence mode="wait">
                    <motion.ul
                        key={active + format}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.45, ease: [0.2, 0.9, 0.2, 1] }}
                        className="divide-y divide-white/25 border-t border-b border-white/25"
                    >
                        {activeCat.items.map((it, idx) => {
                            // For sfizioserie, keep the '—' price. Otherwise, price depends on format.
                            const showPrice = isSfizio
                                ? it.price
                                : isTaglio
                                    ? SITE.tagliePrice
                                    : it.price;
                            return (
                                <RevealOnScroll key={it.name} delay={idx * 0.025} y={16}>
                                    <li
                                        data-testid={MENU.item(
                                            it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
                                        )}
                                        className="menu-row group flex items-center gap-4 md:gap-8 py-5 md:py-6 px-4 md:px-6 cursor-pointer text-[#f4f4ee] hover:text-[#0a0a0a]"
                                    >
                                        <span className="menu-dot inline-block w-2.5 h-2.5 rounded-full bg-[#E63946] shrink-0" />
                                        <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:gap-6">
                                            <h3 className="font-display uppercase text-xl md:text-3xl leading-none whitespace-nowrap">
                                                {it.name}
                                            </h3>
                                            <p className="hidden md:block text-sm md:text-base text-current opacity-70 truncate">
                                                — {it.desc}
                                            </p>
                                            <p className="md:hidden text-xs opacity-70 mt-1">
                                                {it.desc}
                                            </p>
                                        </div>
                                        <span className="font-display text-xl md:text-3xl tabular-nums whitespace-nowrap">
                                            {showPrice === "—" ? "—" : `€ ${showPrice}`}
                                        </span>
                                    </li>
                                </RevealOnScroll>
                            );
                        })}
                    </motion.ul>
                </AnimatePresence>

                {/* Aggiunte + info */}
                {!isSfizio && (
                    <div className="mt-10 md:mt-14 grid md:grid-cols-2 gap-4 md:gap-6">
                        <div className="border-2 border-[#7BB53E] p-5 md:p-6 bg-[#7BB53E]/10">
                            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#7BB53E]">
                                Aggiunte Speciali · € {AGGIUNTE.speciali.price}
                            </p>
                            <p className="mt-3 text-sm md:text-base leading-relaxed text-white/85">
                                {AGGIUNTE.speciali.items.join(" · ")}
                            </p>
                        </div>
                        <div className="border-2 border-white/30 p-5 md:p-6">
                            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFD600]">
                                Altre Aggiunte · € {AGGIUNTE.altre.price}
                            </p>
                            <p className="mt-3 text-sm md:text-base leading-relaxed text-white/70">
                                {AGGIUNTE.altre.note}. Impasti disponibili anche integrali
                                o senza glutine su richiesta.
                            </p>
                        </div>
                    </div>
                )}

                <p className="mt-8 text-xs uppercase tracking-[0.3em] text-white/50">
                    {isTaglio
                        ? "Al taglio · tutti i gusti allo stesso prezzo · scatola omaggio"
                        : "Rotonda · scatola omaggio · pagamento contanti e carte"}
                </p>
            </div>
        </section>
    );
};

export default MenuSection;
